import { css } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Stack, Typography } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { ObjectSchema } from 'yup';
import { MUIAvatar } from '../../../components/Avatar/MUIAvatar';
import { FormFieldWithLabelFactory } from '../../../components/Form/FormFieldAndLabelFactory';
import { getRouteTitle, ROUTES } from '../../../constants/RouteNameMapping';
import { ClientSettingsPreferences } from '../../../data-models/preferences-v3.data-model';
import { clientLogoSchema, clientProfileSchema } from '../../../schemas/ClientSettings.schema';
import { cardStyles } from '../../../theme/component-styles';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { schemaToFormFields } from '../../../util/schema-utils';
import { captureFormValidationError } from '../../../util/SentryUtils';
import { useClientSettings } from './clientSettingsActions';

type ProfileFieldValues = Pick<ClientSettingsPreferences, 'displayName' | 'profileLogoUrl'>;

const formCss = css`
  & .field-label {
    margin-bottom: 0.5rem;
  }
  gap: 1rem;
  margin-block: 1rem;
`;

export function OrgProfile() {
  const { settings, onUpdateLogo, onSaveSettings, rerender, loading } = useClientSettings();

  return (
    <Stack width='100%' height='100%' padding='8px 12px 12px' sx={{ opacity: loading ? 0.5 : 1 }}>
      <Typography variant='h4'>{`${getRouteTitle(ROUTES.ADMIN_PANEL_ORG_PROFILE)}`}</Typography>
      <Stack height='100%' width='100%' overflow='auto' m={'1rem 0'}>
        <ClientSettings
          settings={settings}
          onUpdateLogo={onUpdateLogo}
          onSaveSettings={onSaveSettings}
          rerender={rerender}
        />
      </Stack>
    </Stack>
  );
}

interface IClientSettingsProps {
  settings: ClientSettingsPreferences;
  onUpdateLogo: (file: File) => Promise<string | void>;
  onSaveSettings: (values: ProfileFieldValues) => Promise<void>;
  rerender: number;
}
function ClientSettings({ settings, onUpdateLogo, onSaveSettings, rerender }: IClientSettingsProps) {
  return (
    <Stack gap='1rem' key={rerender}>
      <Stack direction='row' gap='1rem' alignItems='center' style={cardStyles} width='100%'>
        <MUIAvatar
          // force reload with a query param (src in most cases remains the same, but BE will update the file)
          src={settings.logoUrl ? `${settings.logoUrl}?${rerender}` : ''}
          nameParts={settings.name?.split(' ') ?? []}
          size='large'
        />
        <Typography variant='h5'>{settings.name}</Typography>
      </Stack>
      <Stack marginBlock='1rem'>
        <ProfileFieldsForm
          defaults={{
            displayName: settings.displayName ?? '',
            profileLogoUrl: settings.profileLogoUrl ?? '',
          }}
          onSubmit={onSaveSettings}
        />
      </Stack>
      <Stack marginBlock='1rem'>
        <UploadLogoForm onSubmit={onUpdateLogo} />
      </Stack>
    </Stack>
  );
}

interface IProfileFieldsFormProps {
  defaults: ProfileFieldValues;
  onSubmit: (values: ProfileFieldValues) => Promise<void>;
}
const autosaveDebounceMs = 600;
function ProfileFieldsForm({ defaults, onSubmit }: IProfileFieldsFormProps) {
  const schema = useMemo(() => clientProfileSchema(), []);
  const methods = useForm<ProfileFieldValues>({
    resolver: yupResolver(schema as ObjectSchema<ProfileFieldValues>),
    defaultValues: defaults,
  });
  const { control, watch, trigger, getValues } = methods;

  const fields = useMemo(() => schemaToFormFields(schema), [schema]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const subscription = watch((_, { type: eventType }) => {
      if (eventType !== 'change') {
        return;
      }
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(async () => {
        const isValid = await trigger();
        if (!isValid) {
          captureFormValidationError('Org Profile Fields Form', methods.formState.errors);
          return;
        }
        const values = getValues();
        await onSubmit({
          displayName: values.displayName?.trim() || undefined,
          profileLogoUrl: values.profileLogoUrl?.trim() || undefined,
        });
      }, autosaveDebounceMs);
    });
    return () => {
      subscription.unsubscribe();
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [methods, watch, trigger, getValues, onSubmit]);

  return (
    <>
      <Typography variant='h6'>Profile</Typography>
      <FormProvider {...methods}>
        <Stack css={formCss} alignItems='flex-start'>
          {fields.map((field) => (
            <FormFieldWithLabelFactory key={field.key} formField={field} />
          ))}
          <ProfileLogoPreview control={control} />
        </Stack>
      </FormProvider>
    </>
  );
}

interface IProfileLogoPreviewProps {
  control: ReturnType<typeof useForm<ProfileFieldValues>>['control'];
}
function ProfileLogoPreview({ control }: IProfileLogoPreviewProps) {
  const value = useWatch({ control, name: 'profileLogoUrl' });
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    setErrored(false);
  }, [value]);

  const trimmed = value?.trim();
  if (!trimmed || errored) {
    return null;
  }

  return (
    <Stack gap='0.25rem'>
      <Typography variant='caption' color='text.secondary'>
        Preview
      </Typography>
      <Box
        component='img'
        src={trimmed}
        alt='Profile logo preview'
        onError={() => setErrored(true)}
        sx={{
          maxWidth: '12rem',
          maxHeight: '6rem',
          objectFit: 'contain',
          borderRadius: '0.25rem',
          border: '1px solid',
          borderColor: 'divider',
          padding: '0.25rem',
        }}
      />
    </Stack>
  );
}

interface IUploadLogoFormProps {
  onSubmit: (value: File) => Promise<string | void>;
}
function UploadLogoForm({ onSubmit }: IUploadLogoFormProps) {
  const [loading, setLoading] = useState(false);
  const { useKpiFileUpload3 } = useFlags<MaggieFeatureFlags>();
  const schema = useMemo(() => clientLogoSchema(useKpiFileUpload3), [useKpiFileUpload3]);
  const methods = useForm<{ logo: File }>({
    resolver: yupResolver(schema as ObjectSchema<{ logo: File }>),
  });
  const {
    getValues,
    trigger,
    formState: { errors },
    watch,
  } = methods;

  const field = useMemo(() => {
    const field = schemaToFormFields(schema).at(0);
    field!.disabled = loading;
    return field;
  }, [loading, schema]);

  const _onSubmit = useCallback(async () => {
    const isValid = await trigger();
    if (!isValid) {
      captureFormValidationError('Client Settings Form', errors);
      return;
    }
    const { logo } = getValues();
    setLoading(true);
    await onSubmit(logo);
    setLoading(false);
  }, [errors, getValues, onSubmit, trigger]);

  useEffect(() => {
    const subscription = watch((data, { name, type: eventType }) => {
      if (eventType !== 'change' || name != 'logo') {
        return;
      }
      if (!data.logo) {
        return;
      }
      _onSubmit();
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [watch, _onSubmit]);

  return (
    <>
      <Typography variant='h6'>Change Settings</Typography>
      <FormProvider {...methods}>
        <Stack css={formCss}>
          <FormFieldWithLabelFactory formField={field!} />
        </Stack>
      </FormProvider>
    </>
  );
}
